import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import type { TooltipContentProps } from 'recharts/types/component/Tooltip';

import { ReportData } from '../types';
import { buildChartData } from '../utils/chart-data';
import { CHART_COLORS, formatMeasureValue } from '../utils/format-measure';
import { ChartLegend } from './chart-legend';

const TOOLTIP_STYLE = {
  fontSize: 12,
  borderRadius: 6,
  padding: '8px 10px',
  backgroundColor: 'hsl(var(--popover))',
  border: '1px solid hsl(var(--border))',
  color: 'hsl(var(--popover-foreground))',
  boxShadow: '0 2px 8px hsl(var(--border) / 0.4)',
};

interface ElementPieChartProps {
  data: ReportData;
  hiddenSeries?: Set<string>;
}

export const ElementPieChart = ({ data, hiddenSeries }: ElementPieChartProps) => {
  const { t } = useTranslation('authenticated.reports');
  const { rows, measures } = buildChartData(data);
  const firstMeasure = measures[0];
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);

  if (rows.length === 0 || !firstMeasure) {
    return <p className="py-4 text-center text-sm text-muted-foreground">{t('common.noData')}</p>;
  }

  const pieData = rows.map((row, i) => ({
    name: row.__label as string,
    value: hiddenSeries?.has(row.__label as string)
      ? 0
      : typeof row[firstMeasure.key] === 'number'
        ? (row[firstMeasure.key] as number)
        : 0,
    color: CHART_COLORS[i % CHART_COLORS.length],
  }));

  const legendEntries = pieData.map(d => ({ key: d.name, label: d.name, color: d.color }));

  return (
    <div className="grid h-full grid-rows-[1fr_auto]">
      <div className="min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius="80%"
              strokeWidth={1}
              onMouseLeave={() => setHoveredKey(null)}
            >
              {pieData.map(entry => (
                <Cell
                  key={entry.name}
                  fill={entry.color}
                  opacity={hoveredKey === null || hoveredKey === entry.name ? 1 : 0.2}
                  onMouseEnter={() => setHoveredKey(entry.name)}
                />
              ))}
            </Pie>
            <Tooltip
              content={(props: TooltipContentProps) => {
                const { active, payload } = props;
                if (!active || !payload?.[0]) return null;
                const entry = payload[0];
                if (hoveredKey && entry.name !== hoveredKey) return null;
                return (
                  <div style={TOOLTIP_STYLE}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span
                        style={{
                          display: 'inline-block',
                          width: 8,
                          height: 8,
                          borderRadius: 2,
                          backgroundColor: (entry.payload as { color?: string }).color,
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ color: 'hsl(var(--muted-foreground))' }}>{entry.name}</span>
                      <span style={{ fontWeight: 500, marginLeft: 'auto', paddingLeft: 16 }}>
                        {formatMeasureValue(entry.value as number, firstMeasure.type)}
                      </span>
                    </div>
                  </div>
                );
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ChartLegend
        entries={legendEntries}
        hiddenSeries={hiddenSeries}
        hoveredKey={hoveredKey}
        onHover={setHoveredKey}
      />
    </div>
  );
};
